import React from 'react';

export type AdminSparklinePoint = { date?: string; count?: number; value?: number };

type Props = {
  points: AdminSparklinePoint[];
  label: string;
  className?: string;
  height?: number;
};

/** Lightweight SVG sparkline — no chart library. */
export const AdminSparkline: React.FC<Props> = ({
  points,
  label,
  className = '',
  height = 48,
}) => {
  const values = (points || []).map((p) => Number(p.count ?? p.value ?? 0));
  const width = 160;
  if (values.length < 2) {
    return (
      <div className={`text-xs text-gray-500 ${className}`} aria-label={`${label}: insufficient trend data`}>
        No trend yet
      </div>
    );
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const coords = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / span) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');
  const total = values.reduce((a, b) => a + b, 0);
  const last = values[values.length - 1];
  const summary = `${label}: ${values.length} days, total ${total}, latest ${last}`;

  return (
    <figure className={className}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={summary}
        className="overflow-visible"
      >
        <title>{summary}</title>
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={coords}
          className="text-orange-400"
        />
      </svg>
      <figcaption className="sr-only">{summary}</figcaption>
    </figure>
  );
};

export default AdminSparkline;
